import * as React from "react";

import { cn } from "./utils";

interface AutosizeTextareaProps extends React.ComponentProps<"textarea"> {
  /** How many lines the field grows to before it starts scrolling. */
  maxRows?: number;
}

/**
 * A textarea that grows with its content up to `maxRows`, then scrolls.
 *
 * Growth is measured in JS rather than left to CSS `field-sizing: content` —
 * that's Safari 26+ and the iOS target is 16.0. The cap is derived from the
 * computed line-height so it stays correct if the root font size scales.
 */
const AutosizeTextarea = React.forwardRef<HTMLTextAreaElement, AutosizeTextareaProps>(
  ({ className, maxRows = 3, value, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement>(null);

    React.useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    React.useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;

      // Collapse first so scrollHeight reflects the content, not the old height.
      el.style.height = "auto";

      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 24;
      const extra =
        parseFloat(styles.paddingTop) +
        parseFloat(styles.paddingBottom) +
        parseFloat(styles.borderTopWidth) +
        parseFloat(styles.borderBottomWidth);
      const max = maxRows * lineHeight + extra;

      el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    }, [value, maxRows]);

    return (
      <textarea
        ref={innerRef}
        rows={1}
        value={value}
        data-slot="autosize-textarea"
        className={cn(
          // Base styles — mirrors ui/input.tsx. text-base (16px) is required:
          // anything smaller makes iOS zoom the webview on focus.
          // py-[5px] keeps the resting height at 36px — same as the h-9 send /
          // attach buttons next to it, and the same as the Input it replaced.
          "flex w-full min-w-0 bg-surface-L0 border border-border-L2 rounded-[20px] px-3 py-[5px] text-base text-content-primary-bold leading-normal tracking-[0.07px] outline-none transition-[color,box-shadow,border-color]",
          // Sizing is driven by the effect above, never by the drag handle.
          "resize-none overflow-y-auto overscroll-contain",
          // Placeholder
          "placeholder:text-content-primary-subtle",
          // Selection
          "selection:bg-background-primary-bold selection:text-content-primary-inverse",
          // Focus state
          "focus-visible:border-border-focus focus-visible:ring-[1px] focus-visible:ring-border-focus/50",
          // Error state
          "aria-invalid:border-border-negative-bold aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          // Disabled state
          "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
          // Dark mode
          "dark:bg-input/30",
          className,
        )}
        {...props}
      />
    );
  },
);

AutosizeTextarea.displayName = "AutosizeTextarea";

export { AutosizeTextarea };
